const isSupportIntersection = typeof window !== 'undefined' && 'IntersectionObserver' in window;

export class BaseLazyObserver {
    private idCount = 0;
    private nodeKey = 'data-observer-key';
    private observerOptions: IntersectionObserverInit;
    private observer: IntersectionObserver | null = null;
    private isSupportIntersection = isSupportIntersection;
    private callbacks: Map<string, (entries: IntersectionObserverEntry) => void>;

    constructor(options: IntersectionObserverInit) {
        this.observerOptions = {
            ...options,
            rootMargin: options.rootMargin || '300px',
            threshold: options.threshold || 0,
        };
        this.callbacks = new Map();
    }

    public initObserver(chiefCallback?: (entries: IntersectionObserverEntry[]) => void) {
        if (!this.isSupportIntersection) {
            return;
        }

        this.observer = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
            chiefCallback && chiefCallback(entries);
            entries.forEach((entry) => {
                const elementKey = entry.target.getAttribute(this.nodeKey);
                if (elementKey) {
                    const callback = this.callbacks.get(elementKey);
                    callback && callback(entry);
                }
            });
        }, this.observerOptions);
    }

    /**
     * @param node
     * @returns isSupportIntersection
     */
    public observe(node: HTMLElement, callback?: (entry: IntersectionObserverEntry) => void) {
        if (!this.observer) {
            return false;
        }
        const elementKey = ++this.idCount;
        node.setAttribute(this.nodeKey, `${elementKey}`);
        this.observer.observe(node);
        callback && this.callbacks.set(`${elementKey}`, callback);
        return true;
    }

    public unobserve(node: HTMLElement) {
        this.observer?.unobserve(node);
        const elementKey = node.getAttribute(this.nodeKey);
        if (elementKey) {
            this.callbacks.delete(elementKey);
        }
    }

    public disconnect() {
        this.observer?.disconnect();
        this.callbacks.clear();
        this.idCount = 0;
    }
}
